import { Add, AppRegistration, Edit } from '@mui/icons-material'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import TablePagination from '@mui/material/TablePagination'
import { LabelField } from 'components'
import { enqueueSnackbar } from 'notistack'
import { Chip } from 'components/chips'
import { InboundTripDto, InboundTripPAPSDto } from 'dtos'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PAPSManagementAddEditDialog from './PAPSManagementAddEditDialog'
import * as controllers from 'controllers'
import { InboundTripPAPSParameters } from 'parameters'
import { formatAddress, useDebounce } from 'utils'
import { DEBOUNCE_DELAY } from 'localConstants'
import { useLoadingState } from 'hooks'
import { useAuth } from 'context'

interface PAPSManagementListCardProps {
  inboundTrip: InboundTripDto
}

export default function PAPSManagementListCard({ inboundTrip }: PAPSManagementListCardProps) {
  const { permissions } = useAuth()
  const navigate = useNavigate()

  const [inboundTripPAPS, setInboundTripPAPS] = useState<InboundTripPAPSDto[]>([])
  const [initialValues, setInitialValues] = useState<InboundTripPAPSDto>(new InboundTripPAPSDto())
  const [isPAPSSaving, setIsPAPSSaving] = React.useState<boolean>(false)
  const [loadingState, setLoadingState] = useLoadingState({ isGettingInboundTripPAPS: false })
  const [PAPSManagementAddEditDialogOpen, setPAPSManagementAddEditDialogOpen] = React.useState<boolean>(false)
  const [parameters, setParameters] = useState<InboundTripPAPSParameters>({ page: 0, pageSize: 3, order: 'asc', orderBy: '' })
  const [totalCount, setTotalCount] = useState<number>(0)

  const handleDownloadAbramsAutoInvoiceFile = async (fileName: string, inboundTripPAPSGuid: string | undefined, inboundTripGuid: string | undefined) => {
    if (!inboundTripGuid || !inboundTripPAPSGuid || !fileName) {
      console.error('Missing required parameters for downloading the file.')
      return
    }

    try {
      const blob = await controllers.inboundTrips.getInboundTripPAPSAbramsAutoInvoice(inboundTripGuid, inboundTripPAPSGuid)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading Abrams Auto Invoice:', error)
    }
  }

  const getInboundTripPAPS = useDebounce((inboundTripGuid: string, parameters: InboundTripPAPSParameters) => {
    setLoadingState('isGettingInboundTripPAPS', true)
    controllers
      .getInboundTripPAPS(inboundTripGuid, parameters)
      .then(response => {
        setInboundTripPAPS(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setLoadingState('isGettingInboundTripPAPS', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (inboundTrip.inboundTripGuid) {
      getInboundTripPAPS(inboundTrip.inboundTripGuid, parameters)
    }
  }, [getInboundTripPAPS, inboundTrip, parameters])

  return (
    <>
      <PAPSManagementAddEditDialog
        isLoading={isPAPSSaving}
        initialValues={initialValues}
        inboundTrip={inboundTrip}
        onClose={() => {
          setPAPSManagementAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsPAPSSaving(true)
          controllers
            .createInboundTripPAPS(inboundTrip.inboundTripGuid!, values)
            .then(response => {
              enqueueSnackbar('PAPS Saved Successfully!', { variant: 'success' })
              setPAPSManagementAddEditDialogOpen(false)
              getInboundTripPAPS(inboundTrip.inboundTripGuid, parameters)
            })
            .finally(() => {
              setIsPAPSSaving(false)
            })
        }}
        open={PAPSManagementAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  PAPS Management
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Add />}
                  onClick={() => {
                    setIsPAPSSaving(false)
                    setPAPSManagementAddEditDialogOpen(true)
                  }}
                  variant='text'
                  disabled={inboundTrip.isLocked}
                >
                  ADD PAPS
                </Button>
              </Grid>
            </Grid>

            {loadingState.isGettingInboundTripPAPS && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingInboundTripPAPS &&
              inboundTripPAPS.map(paps => (
                <Grid item xs={12}>
                  <Card variant='outlined'>
                    <CardContent>
                      <Grid container alignItems='center' spacing={1}>
                        <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                          <Grid item xs={12} sm container spacing={0}>
                            <Grid item>
                              <Typography sx={{ mr: 1 }} variant='h6'>
                                PAPS #{paps.papsNumber}
                              </Typography>
                            </Grid>

                            <Grid item>{paps.isLocked ? <Chip variant='red'>Locked</Chip> : <Chip variant='green'>Unlocked</Chip>}</Grid>
                          </Grid>

                          <Grid item xs={12} sm='auto' container alignItems='flex-end' justifyContent='flex-end' direction='column' spacing={1}>
                            {/* <Grid item>
                              <Button
                                endIcon={<Edit />}
                                onClick={() => {
                                  setIsPAPSSaving(false)
                                  setPAPSManagementAddEditDialogOpen(true)
                                }}
                                variant='text'
                                size='small'
                                // disabled={isLocked}
                              >
                                EDIT
                              </Button>
                            </Grid> */}

                            <Grid item>
                              <Button
                                endIcon={<AppRegistration />}
                                onClick={() => {
                                  navigate(`/inbound-trips/${inboundTrip.inboundTripGuid}/paps/${paps.inboundTripPAPSGuid}/manage-vehicles`)
                                }}
                                variant='text'
                                // disabled={isLocked}
                              >
                                MANAGE PAPS
                              </Button>
                            </Grid>

                            {/* <Grid item>
                              <Button
                                endIcon={initialValues.isLocked ? <LockIcon /> : <LockOpenIcon />}
                                onClick={() => {}}
                                variant='text'
                                disabled={inboundTrip.isLocked}
                              >
                                {initialValues.isLocked ? 'UNLOCK PAPS' : 'LOCK PAPS'}
                              </Button>
                            </Grid> */}
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                          <Grid item xs={6}>
                            <LabelField label='# of Vehicles' value={paps.numberOfVehiclesInPAPS} />
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField label='Buyer Company' value={paps.buyerCompany?.name} />
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField label='Vehicle Destination Company' value={paps.vehicleDestinationCompany?.name} />
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField
                            label='Vehicle Destination Company Address'
                            value={paps.vehicleDestinationCompanyAddress ? formatAddress(paps.vehicleDestinationCompanyAddress) : ''}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField label='Title Destination Company' value={paps.titleDestinationCompany?.name} />
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField
                            label='Title Destination Company Address'
                            value={paps.titleDestinationCompanyAddress ? formatAddress(paps.titleDestinationCompanyAddress) : ''}
                          />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                          <Typography
                            variant='h6'
                            component='a'
                            // onClick={() =>
                            //   handleDownloadAbramsAutoInvoiceFile(paps.abramsAutoInvoiceFileName || 'AbramsAutoInvoice.pdf', paps.inboundTripPAPSGuid)
                            // }
                            onClick={() =>
                              handleDownloadAbramsAutoInvoiceFile(
                                paps.abramsAutoInvoiceFileName || 'AbramsAutoInvoice.pdf',
                                paps.inboundTripPAPSGuid,
                                inboundTrip.inboundTripGuid
                              )
                            }
                            sx={{
                              textDecoration: 'underline',
                              color: 'primary.main',
                              cursor: 'pointer'
                            }}
                          >
                            Download Abrams Auto Invoice
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage='Cards per page:'
            rowsPerPageOptions={[
              { label: '1', value: 1 },
              { label: '3', value: 3 },
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </CardContent>
      </Card>
    </>
  )
}
